import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { trackEvent, EVENT_TYPE_PAGE, trackGA4Event } from "../utils/trackEvent.jsx";
import { Button } from '@/components/ui/button';
import { CheckCircle2, ArrowRight } from 'lucide-react';

// Helper component to render text with styled HTML tags
const StyledText = ({ text, className = "" }) => {
  // Parse <b> and <strong> tags and replace with styled spans
  const parts = text.split(/(<(?:b|strong)>.*?<\/(?:b|strong)>)/gi);
  
  return (
    <span className={className}>
      {parts.map((part, index) => {
        const boldMatch = part.match(/<(?:b|strong)>(.*?)<\/(?:b|strong)>/i);
        if (boldMatch) {
          const boldText = boldMatch[1];
          return (
            <strong key={index} className="text-emerald-600 font-bold">
              {boldText}
            </strong>
          );
        }
        return <span key={index}>{part}</span>;
      })}
    </span>
  );
};

function BusinessAndNonprofits() {
  const navigate = useNavigate();

  useEffect(() => {
    trackEvent(EVENT_TYPE_PAGE);
  }, []);

  const handleStartApplication = () => {
    trackGA4Event('start_application_clicked', {
      button_location: 'business_and_nonprofits_page',
      button_text: 'Start My Application'
    });
    
    // Navigate to home page and scroll to form
    navigate('/home');
    setTimeout(() => {
      const formElement = document.getElementById('loan-form');
      if (formElement) {
        const formPosition = formElement.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({
          top: formPosition - 20,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden min-h-screen flex items-center" aria-label="Hero section">
        {/* Background Image */}
        <div 
          className="absolute inset-0 z-0 brightness-110 contrast-105"
          style={{ 
            backgroundImage: `url("/uploads/hero-finance.jpg")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        
        {/* Professional Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/35 to-black/50" />
        
        {/* Subtle Green Accent Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a4a1a]/15 via-transparent to-[#2d6a2d]/20" />
        
        {/* Content */}
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 lg:py-32 w-full">
          <div className="text-center animate-fade-in space-y-8 md:space-y-10">
            {/* Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-[1.1] drop-shadow-[0_4px_20px_rgba(0,0,0,0.8)] tracking-tight">
              Halal Financing Backed by Real Institutions
            </h1>
            
            {/* Description */}
            <div className="max-w-3xl mx-auto px-4">
              <p className="text-lg md:text-xl lg:text-2xl text-white/95 leading-relaxed drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]">
                HalalCred connects you to riba-free financing delivered by CDFIs and grounded in Craft3's six-year track record in Islamic financing.
              </p>
            </div>

            {/* Key Message */}
            <div className="py-4 md:py-6">
              <div className="inline-block px-6 py-3 md:px-8 md:py-4 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20">
                <p className="text-base md:text-lg lg:text-xl text-white leading-tight drop-shadow-[0_2px_12px_rgba(0,0,0,0.8)]">
                  You are not testing an unproven product.
                </p>
                <p className="text-base md:text-lg lg:text-xl font-bold text-white leading-tight drop-shadow-[0_2px_12px_rgba(0,0,0,0.8)] mt-2">
                  You are accessing financing that has already funded mosques, nonprofits, and businesses across the U.S.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Makes This Different Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-emerald-50 via-green-50 to-emerald-100" aria-label="What makes this different">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a4a1a] mb-4 leading-tight">
              What Makes This Different
            </h2>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 lg:p-10 border border-emerald-100 mb-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              <div className="flex items-start gap-4 group">
                <div className="flex-shrink-0 mt-1">
                  <CheckCircle2 className="w-6 h-6 md:w-7 md:h-7 text-emerald-600 group-hover:text-emerald-700 transition-colors" />
                </div>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed group-hover:text-gray-900 transition-colors">
                  Financing structures already in market use
                </p>
              </div>
              
              <div className="flex items-start gap-4 group">
                <div className="flex-shrink-0 mt-1">
                  <CheckCircle2 className="w-6 h-6 md:w-7 md:h-7 text-emerald-600 group-hover:text-emerald-700 transition-colors" />
                </div>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed group-hover:text-gray-900 transition-colors">
                  Transactions reviewed and certified by Islamic scholars
                </p>
              </div>
              
              <div className="flex items-start gap-4 group">
                <div className="flex-shrink-0 mt-1">
                  <CheckCircle2 className="w-6 h-6 md:w-7 md:h-7 text-emerald-600 group-hover:text-emerald-700 transition-colors" />
                </div>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed group-hover:text-gray-900 transition-colors">
                  Underwritten and serviced by experienced mission driven community lenders
                </p>
              </div>
              
              <div className="flex items-start gap-4 group">
                <div className="flex-shrink-0 mt-1">
                  <CheckCircle2 className="w-6 h-6 md:w-7 md:h-7 text-emerald-600 group-hover:text-emerald-700 transition-colors" />
                </div>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed group-hover:text-gray-900 transition-colors">
                  Designed to support long-term ownership and stability
                </p>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center">
            <Button
              onClick={handleStartApplication}
              size="lg"
              className="group bg-emerald-600 hover:bg-emerald-700 text-white px-10 py-6 text-lg font-bold shadow-2xl hover:shadow-[0_8px_30px_rgba(26,74,26,0.3)] hover:scale-105 transition-all duration-300 rounded-xl"
            >
              Start My Application
              <ArrowRight className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </section>

      {/* Fade-in Animation Styles */}
      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.8s ease-out;
        }
      `}</style>
    </div>
  );
}

export default BusinessAndNonprofits;
